/**
 * @file Moteur audio WebAudio : sons 100% synthetises depuis config/audio (aucun fichier), deverrouillage au premier geste, volume/mute, limitation de polyphonie,
 * musique d'ambiance procedurale (ProceduralMusic) sur son propre bus (reglage Musique), suspendue quand l'onglet est cache.
 */
import type { SfxId, SfxRecipe } from '@config/types';
import { SFX_RECIPES } from '@config/index';
import { ProceduralMusic } from './ProceduralMusic';

/** Intervalle minimal entre deux lectures du MEME son (ms). */
const MIN_GAP_MS = 35;
/** Polyphonie maximale (un "voix" = un appel de play() encore audible). */
const MAX_VOICES = 24;
/** Duree du buffer de bruit blanc partage (secondes). */
const NOISE_SECONDS = 1;
/** Enveloppe : attaque par defaut (s) et niveau plancher de la decroissance exponentielle. */
const DEFAULT_ATTACK = 0.004;
const ENV_FLOOR = 0.0001;
/** Coupure passe-bas par defaut (Hz) quand la recette n'en precise pas. */
const DEFAULT_FILTER_HZ = 16000;

type AudioContextCtor = typeof AudioContext;

const contextCtor = (): AudioContextCtor | null => {
  const w = globalThis as unknown as { AudioContext?: AudioContextCtor; webkitAudioContext?: AudioContextCtor };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
};

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  /** Derniere lecture par son (ms, horloge audio) : anti-spam (ex. 'shoot' max 1 toutes les 35 ms). */
  private readonly lastPlay = new Map<SfxId, number>();
  private voices = 0;
  private musicBus: GainNode | null = null;
  private music: ProceduralMusic | null = null;
  private paused = false;
  volume = 0.8;
  muted = false;
  musicVolume = 0.5;

  constructor(private readonly recipes: Record<SfxId, SfxRecipe> = SFX_RECIPES) {}

  /**
   * Cree l'AudioContext paresseusement au premier geste (politique autoplay), master GainNode (+ compresseur anti-saturation),
   * buffer de bruit blanc (1 s) partage pour wave 'noise'. Idempotent : reprend le contexte s'il est suspendu.
   */
  unlock(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume().catch(() => undefined);
      return;
    }
    const Ctor = contextCtor();
    if (!Ctor) return;
    let ctx: AudioContext;
    try {
      ctx = new Ctor();
    } catch {
      return;
    }
    const master = ctx.createGain();
    master.gain.value = this.muted ? 0 : this.volume;
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -14;
    comp.knee.value = 12;
    comp.ratio.value = 6;
    comp.attack.value = 0.003;
    comp.release.value = 0.12;
    master.connect(comp);
    comp.connect(ctx.destination);

    const frames = Math.max(1, Math.floor(ctx.sampleRate * NOISE_SECONDS));
    const buf = ctx.createBuffer(1, frames, ctx.sampleRate);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < frames; i++) ch[i] = Math.random() * 2 - 1;

    // bus musique separe (hors compresseur : les effets ne la font pas pomper), fondu d'entree doux
    const musicBus = ctx.createGain();
    musicBus.gain.value = 0;
    musicBus.connect(ctx.destination);

    this.ctx = ctx;
    this.master = master;
    this.noiseBuffer = buf;
    this.musicBus = musicBus;
    this.music = new ProceduralMusic(ctx, musicBus);
    this.updateMusic(1.2);
    if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
  }

  /**
   * Joue la recette : OscillatorNode (ou BufferSource bruit) -> BiquadFilter lowpass (filterHz) -> Gain (enveloppe attack
   * lineaire puis decroissance exponentielle sur duration) -> master. Rampe de frequence freq[0] -> freq[1].
   * `chord` : un oscillateur par demi-ton (freq * 2^(n/12)), gain divise par le nombre de notes. `pitch` multiplie freq
   * (variation +-5% pour les tirs). Anti-spam 35 ms par id ; max 24 voix simultanees. No-op si muted ou non deverrouille.
   */
  play(id: SfxId, pitch = 1, gain = 1): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master || this.muted || this.volume <= 0) return;
    if (ctx.state !== 'running') {
      if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
      return;
    }
    const r = this.recipes[id];
    if (!r) return;
    const nowMs = ctx.currentTime * 1000;
    const last = this.lastPlay.get(id);
    if (last !== undefined && nowMs - last < MIN_GAP_MS) return;
    if (this.voices >= MAX_VOICES) return;
    this.lastPlay.set(id, nowMs);

    const t0 = ctx.currentTime;
    const dur = Math.max(0.02, r.duration);
    const tEnd = t0 + dur;
    const attack = Math.min(r.attack ?? DEFAULT_ATTACK, dur * 0.5);
    const notes = r.chord && r.chord.length > 0 ? r.chord : [0];
    const peak = Math.max(ENV_FLOOR * 2, (r.gain * gain) / notes.length);

    const env = ctx.createGain();
    env.gain.setValueAtTime(0, t0);
    env.gain.linearRampToValueAtTime(peak, t0 + attack);
    env.gain.exponentialRampToValueAtTime(ENV_FLOOR, tEnd);

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.Q.value = 0.7;
    filter.connect(env);
    env.connect(master);

    const sources: AudioScheduledSourceNode[] = [];
    if (r.wave === 'noise') {
      const src = ctx.createBufferSource();
      if (this.noiseBuffer) src.buffer = this.noiseBuffer;
      // le bruit "balaie" le filtre de freq[0] a freq[1] (borne par filterHz) : grondement d'explosion, souffle de chute...
      const cap = r.filterHz ?? DEFAULT_FILTER_HZ;
      filter.frequency.setValueAtTime(Math.min(cap, Math.max(40, r.freq[0] * pitch)), t0);
      filter.frequency.exponentialRampToValueAtTime(Math.min(cap, Math.max(40, r.freq[1] * pitch)), tEnd);
      src.connect(filter);
      src.start(t0, Math.random() * (NOISE_SECONDS - Math.min(dur, NOISE_SECONDS * 0.9)));
      src.stop(tEnd + 0.02);
      sources.push(src);
    } else {
      filter.frequency.value = r.filterHz ?? DEFAULT_FILTER_HZ;
      for (const semi of notes) {
        const ratio = Math.pow(2, semi / 12) * pitch;
        const osc = ctx.createOscillator();
        osc.type = r.wave;
        osc.frequency.setValueAtTime(Math.max(20, r.freq[0] * ratio), t0);
        if (r.freq[1] !== r.freq[0]) osc.frequency.exponentialRampToValueAtTime(Math.max(20, r.freq[1] * ratio), tEnd);
        osc.connect(filter);
        osc.start(t0);
        osc.stop(tEnd + 0.02);
        sources.push(osc);
      }
    }

    this.voices++;
    const first = sources[0];
    if (first) {
      first.onended = () => {
        this.voices = Math.max(0, this.voices - 1);
        for (const s of sources) s.disconnect();
        filter.disconnect();
        env.disconnect();
      };
    } else {
      this.voices = Math.max(0, this.voices - 1);
    }
  }

  setVolume(v: number, muted: boolean): void {
    this.volume = Math.min(1, Math.max(0, v));
    this.muted = muted;
    if (this.master) this.master.gain.value = muted ? 0 : this.volume;
    this.updateMusic();
  }

  /** Volume de la musique d'ambiance (0 = coupee : plus aucune note planifiee). */
  setMusicVolume(v: number): void {
    this.musicVolume = Math.min(1, Math.max(0, v));
    this.updateMusic();
  }

  /** Onglet cache / app en arriere-plan : contexte suspendu (aucun son, pas de batterie), musique reprise au retour. */
  setPaused(paused: boolean): void {
    this.paused = paused;
    const ctx = this.ctx;
    if (!ctx || ctx.state === 'closed') return;
    if (paused) {
      this.music?.stop();
      void ctx.suspend().catch(() => undefined);
    } else {
      void ctx.resume().catch(() => undefined);
      this.updateMusic(0.8);
    }
  }

  /** Demarre / arrete la boucle et ajuste le gain du bus en douceur (pas de clic). */
  private updateMusic(fade = 0.25): void {
    const ctx = this.ctx;
    const bus = this.musicBus;
    const music = this.music;
    if (!ctx || !bus || !music) return;
    const on = !this.muted && !this.paused && this.musicVolume > 0;
    // courbe perceptive : le curseur a mi-course reste discret
    const level = on ? Math.pow(this.musicVolume, 1.5) : 0;
    bus.gain.cancelScheduledValues(ctx.currentTime);
    bus.gain.setTargetAtTime(level, ctx.currentTime, fade / 3);
    if (on && !music.playing) music.start();
    else if (!on && music.playing) music.stop();
  }

  /** Ferme l'AudioContext (dispose de l'app). */
  dispose(): void {
    const ctx = this.ctx;
    this.music?.stop();
    this.music = null;
    this.musicBus = null;
    this.ctx = null;
    this.master = null;
    this.noiseBuffer = null;
    this.voices = 0;
    this.lastPlay.clear();
    if (ctx && ctx.state !== 'closed') void ctx.close().catch(() => undefined);
  }
}
